import { useEffect, useMemo } from "react";
import {
  buildAbsoluteUrl,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
} from "../constants/seo";

const DEFAULT_TITLE = `${SITE_NAME} | Office & School Supplies UAE`;
const DEFAULT_DESCRIPTION =
  "SG Office Supplies — trusted since 2010 for office stationery, school essentials, printing & branding across the UAE.";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"][data-seo-managed]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo-managed", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo-managed]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo-managed", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  document
    .querySelectorAll(`script[data-seo-jsonld-id="${id}"]`)
    .forEach((node) => node.remove());

  if (!data) return;

  const payloads = Array.isArray(data) ? data.filter(Boolean) : [data];
  payloads.forEach((payload, index) => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-managed", "true");
    el.setAttribute("data-seo-jsonld-id", id);
    el.id = payloads.length > 1 ? `${id}-${index}` : id;
    el.textContent = JSON.stringify(payload);
    document.head.appendChild(el);
  });
}

function cleanupManagedNodes(jsonLdId) {
  document
    .querySelectorAll(`script[data-seo-jsonld-id="${jsonLdId}"]`)
    .forEach((node) => node.remove());
  document.querySelectorAll("[data-seo-managed]").forEach((node) => {
    if (node.tagName !== "SCRIPT") node.remove();
  });
}

/**
 * Sets document title, meta tags, canonical, Open Graph, Twitter cards, and JSON-LD.
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd = null,
  jsonLdId = "seo-jsonld",
}) {
  const fullTitle = title || DEFAULT_TITLE;
  const canonical = buildAbsoluteUrl(path);
  const imageUrl = image.startsWith("http") ? image : buildAbsoluteUrl(image);
  const jsonLdSerialized = useMemo(
    () => (jsonLd ? JSON.stringify(jsonLd) : ""),
    [jsonLd]
  );

  useEffect(() => {
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", imageUrl);

    upsertMeta("name", "twitter:card", "summary");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);

    const parsedJsonLd = jsonLdSerialized ? JSON.parse(jsonLdSerialized) : null;
    upsertJsonLd(jsonLdId, parsedJsonLd);

    return () => cleanupManagedNodes(jsonLdId);
  }, [
    fullTitle,
    description,
    canonical,
    imageUrl,
    noindex,
    jsonLdId,
    jsonLdSerialized,
  ]);

  return null;
}
